import { useCallback, useEffect, useRef, useState } from 'react';
import resolveApiBase from '../../tradingCockpit/apiBase';

const API_BASE = resolveApiBase();
const API_KEY = process.env.REACT_APP_API_KEY;
const POLL_MS = 2000;

const resolveUrl = (path) => {
    if (!API_BASE) return path;
    const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Full Dhruva /state payload (the generic monitor hook drops the `dhruva` block):
 * { summary, layers, events, accounts, tradeHistory, dhruva: { decision, gateHistory, days, dayEvents, legs } }
 */
export function useDhruvaState(enabled = true) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [fetchedAt, setFetchedAt] = useState(null);
    const alive = useRef(true);

    const fetchState = useCallback(async () => {
        try {
            const resp = await fetch(resolveUrl('/api/strategies/dhruva/state'), {
                headers: { Accept: 'application/json', ...(API_KEY ? { 'x-api-key': API_KEY } : {}) },
                cache: 'no-store',
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const payload = await resp.json();
            if (!alive.current) return;
            setData(payload);
            setError(null);
            setFetchedAt(new Date().toISOString());
        } catch (err) {
            if (alive.current) setError(err.message || String(err));
        }
    }, []);

    useEffect(() => {
        alive.current = true;
        if (!enabled) return () => { alive.current = false; };
        fetchState();
        const handle = window.setInterval(fetchState, POLL_MS);
        return () => {
            alive.current = false;
            window.clearInterval(handle);
        };
    }, [enabled, fetchState]);

    return { data, error, fetchedAt, refresh: fetchState };
}

export default useDhruvaState;
